import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-indigo-700 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">Nexus Leads</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-heading font-bold text-slate-900 mb-2">Cookie Policy</h1>
        <p className="text-slate-500 mb-8">Last updated: January 2026</p>

        <div className="bg-white rounded-2xl border border-slate-200 p-8 space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">1. What Are Cookies?</h2>
            <p className="text-slate-600 leading-relaxed">
              Cookies are small text files that are stored on your device when you visit a website. 
              They help websites remember your preferences, understand how you use the site, and 
              improve your overall experience. We use cookies and similar technologies to provide, 
              protect, and improve our Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Types of Cookies We Use</h2>
            
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium text-slate-800 mb-2">Essential Cookies</h3>
                <p className="text-slate-600 text-sm">
                  Required for the Service to function properly. These include authentication tokens, 
                  session management, and security cookies. You cannot opt out of essential cookies.
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium text-slate-800 mb-2">Functional Cookies</h3>
                <p className="text-slate-600 text-sm">
                  Remember your preferences and settings, such as language selection, theme preferences, 
                  and saved searches. These enhance your experience but are not strictly necessary.
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium text-slate-800 mb-2">Analytics Cookies</h3>
                <p className="text-slate-600 text-sm">
                  Help us understand how visitors interact with our Service by collecting information 
                  about pages visited, time spent, and navigation patterns. This data is aggregated 
                  and anonymous.
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium text-slate-800 mb-2">Performance Cookies</h3>
                <p className="text-slate-600 text-sm">
                  Monitor and improve the performance of our Service, including page load times, 
                  error tracking, and resource optimization.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">3. Cookies We Use</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm mt-4">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-800">Cookie Name</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-800">Purpose</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-800">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-slate-600">
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 font-mono text-xs">token</td>
                    <td className="py-3 px-4">Authentication and session management</td>
                    <td className="py-3 px-4">7 days</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 font-mono text-xs">preferences</td>
                    <td className="py-3 px-4">User preferences and settings</td>
                    <td className="py-3 px-4">1 year</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 font-mono text-xs">_analytics</td>
                    <td className="py-3 px-4">Usage analytics and statistics</td>
                    <td className="py-3 px-4">30 days</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 font-mono text-xs">cookie_consent</td>
                    <td className="py-3 px-4">Remembers your cookie preferences</td>
                    <td className="py-3 px-4">1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Third-Party Cookies</h2>
            <p className="text-slate-600 leading-relaxed mb-3">
              Some cookies are placed by third-party services that appear on our pages:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong>Google:</strong> Analytics and performance monitoring (if enabled)</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-3">
              These third parties have their own privacy policies governing the use of their cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Managing Cookies</h2>
            <p className="text-slate-600 leading-relaxed mb-3">
              You can control and manage cookies in several ways:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong>Browser Settings:</strong> Most browsers allow you to refuse or delete cookies through settings</li>
              <li><strong>Our Settings:</strong> Use our cookie preference center (if available) to manage non-essential cookies</li>
              <li><strong>Opt-Out Links:</strong> Use third-party opt-out tools for analytics cookies</li>
            </ul>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mt-4">
              <p className="text-amber-800 text-sm">
                <strong>Note:</strong> Blocking essential cookies may prevent you from using certain features 
                of our Service, including logging in and saving your leads.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Local Storage</h2>
            <p className="text-slate-600 leading-relaxed">
              In addition to cookies, we use local storage to store authentication tokens and user 
              preferences. Local storage data persists until you clear your browser data or we 
              programmatically remove it (e.g., on logout).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Updates to This Policy</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update this Cookie Policy from time to time to reflect changes in our practices 
              or for legal reasons. We will notify you of any material changes by posting the updated 
              policy on this page.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have questions about our use of cookies, please contact us at{' '}
              <a href="mailto:support@nexusleads.us" className="text-indigo-600 hover:underline">
                support@nexusleads.us
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 bg-slate-900 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-400 text-sm">
            © 2026 Nexus Leads. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicy;
